"use client";

import { CalendarCheck2, CalendarX2 } from "lucide-react";
import dynamic from "next/dynamic";
import { Activity, Suspense } from "react";
import type { EventPageData } from "@/actions/event";
import type { PackForSaleRow } from "@/actions/packs";
import type { TaggableEvent } from "@/actions/post-context";
import type { CommentThreadViewer } from "@/components/posts/comment-thread.client";
import { DetailInfoIcon } from "@/components/ui/detail-info-row";
import { ListRowSkeleton } from "@/components/ui/list-row-skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { PackStripContext } from "@/features/packs/components/pack-strip";
import type { ViewerPackCoverage } from "@/features/packs/lib/eligibility";
import { cn } from "@/lib/utils";
import { EventDetailsTabClient } from "./event-details-tab.client";
import type { HiiveSummary, NormalizedOccurrence } from "./event-page.shared";
import { SlotSelectors } from "./event-slot-selectors";

const EventActivityTabClient = dynamic(() =>
  import("./event-activity-tab.client").then(
    (module) => module.EventActivityTabClient,
  ),
);

const EventDiscussionTabClient = dynamic(() =>
  import("./event-discussion-tab.client").then(
    (module) => module.EventDiscussionTabClient,
  ),
);

const EventMembersTabClient = dynamic(() =>
  import("./event-members-tab.client").then(
    (module) => module.EventMembersTabClient,
  ),
);

const EventPhotosTabClient = dynamic(() =>
  import("./event-photos-tab.client").then(
    (module) => module.EventPhotosTabClient,
  ),
);

interface EventTabsSectionProps {
  /**
   * `drawer` (phone) pins the tab bar inside the sheet's own scroller;
   * `page` (desktop) pins it to the window, directly under the top bar.
   */
  layout?: "drawer" | "page";
  currentTab: string;
  onTabChange: (value: string) => void;
  /** The discussion composer only renders while the drawer is expanded. */
  discussionComposerVisible: boolean;
  /** Expands the drawer to its full snap (collapsed-composer tap target). */
  onExpandDrawer: () => void;
  headerHeight: number;
  tabsRef: React.RefObject<HTMLDivElement | null>;
  minContentHeight: number;
  hiiveSlug: string;
  showSlotSelectors: boolean;
  dateKeys: string[];
  selectedDateKey: string | null;
  handleDateChange: (date: string) => void;
  occurrencesForSelectedDate: NormalizedOccurrence[];
  eventPattern: string;
  selectedOccurrence: NormalizedOccurrence | null;
  handleSlotChange: (slotId: string) => void;
  activeOccurrence: {
    id: number;
    startTime: Date;
    endTime: Date;
    timezone: string | null;
    attendeeCount: number;
  } | null;
  activeScheduleOccurrence: NormalizedOccurrence | null;
  scheduleOccurrences: NormalizedOccurrence[];
  canViewAttendeeList: boolean;
  canViewAttendeeCount: boolean;
  showAttendeesTab: boolean;
  activities: EventPageData["event"]["activities"];
  module: EventPageData["event"]["module"];
  hasModules: boolean;
  modulesTabLabel: string;
  userId?: string;
  viewer?: CommentThreadViewer;
  hostUserIds: string[];
  /** Viewer's closest confirmed occurrence for this event (album composer tag). */
  viewerAlbumTag: TaggableEvent | null;
  event: EventPageData["event"];
  distanceKm: number | null;
  hiive: HiiveSummary;
  hiiveMemberCount: number;
  hiiveEventCount: number;
  packs: PackForSaleRow[];
  packStripContext?: PackStripContext | null;
  viewerPackHolding?: ViewerPackCoverage | null;
  /** Discussion and photo counts. Zero renders no badge — see TabCount. */
  tabCounts: { discussion: number; photos: number };
  /** Whether any date is still bookable — decides "date" vs "event" wording. */
  hasUpcomingDates: boolean;
}

/**
 * Shown above the tabs' content when the selected date is no longer on.
 *
 * Speaks about the EVENT when nothing else is coming, and about the date only
 * when another one is there to pick. "This occurrence has ended. You're viewing
 * historical info." was the old line: database vocabulary, and no answer to
 * the one question a visitor has — can I still go?
 */
function PastDateNotice({
  status,
  hasUpcomingDates,
}: {
  status: NormalizedOccurrence["status"];
  hasUpcomingDates: boolean;
}) {
  const { icon, title, subtitle } =
    status === "canceled"
      ? {
          icon: CalendarX2,
          title: hasUpcomingDates
            ? "This date was canceled"
            : "This event was canceled",
          subtitle: hasUpcomingDates
            ? "Pick another date to book."
            : "The organiser called it off.",
        }
      : status === "completed"
        ? {
            icon: CalendarCheck2,
            title: hasUpcomingDates
              ? "This date has passed"
              : "This event has ended",
            subtitle: hasUpcomingDates
              ? "Pick another date to book."
              : "Photos and the discussion are still open.",
          }
        : {
            icon: CalendarX2,
            title: "Bookings for this date are closed",
            subtitle: hasUpcomingDates ? "Pick another date to book." : null,
          };

  return (
    <div className="mt-4 mb-6 px-3">
      <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-surface/60 p-3 dark:border-white/[0.08] dark:bg-white/[0.04]">
        <DetailInfoIcon icon={icon} tone="muted" />
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-text-primary">{title}</p>
          {subtitle ? (
            <p className="mt-0.5 text-sm text-text-secondary">{subtitle}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * How much is behind a tab, when there is anything behind it.
 *
 * Rendered only above zero, on purpose. "Discussion (0)" and "Photos (0)"
 * advertise two empty rooms on every brand-new event, which reads as a dead
 * page and discourages the first post — exactly the opposite of what an empty
 * discussion needs. Above zero the number is a reason to tap.
 *
 * The count sits in its own span rather than in the label string so it can be
 * dimmed independently and never inherits the active tab's weight.
 */
function TabCount({ value }: { value: number }) {
  if (value <= 0) return null;
  return (
    <span className="ml-1.5 text-[0.75em] font-normal text-text-secondary tabular-nums">
      {value}
    </span>
  );
}

export const EventTabsSection = ({
  layout = "drawer",
  currentTab,
  onTabChange,
  discussionComposerVisible,
  onExpandDrawer,
  headerHeight,
  tabsRef,
  minContentHeight,
  hiiveSlug,
  showSlotSelectors,
  dateKeys,
  selectedDateKey,
  handleDateChange,
  occurrencesForSelectedDate,
  eventPattern,
  selectedOccurrence,
  handleSlotChange,
  activeOccurrence,
  activeScheduleOccurrence,
  scheduleOccurrences,
  canViewAttendeeList,
  canViewAttendeeCount,
  showAttendeesTab,
  activities,
  module,
  hasModules,
  modulesTabLabel,
  userId,
  viewer,
  hostUserIds,
  viewerAlbumTag,
  event,
  distanceKm,
  hiive,
  hiiveMemberCount,
  hiiveEventCount,
  packs,
  packStripContext,
  viewerPackHolding,
  tabCounts,
  hasUpcomingDates,
}: EventTabsSectionProps) => {
  const isPage = layout === "page";
  const stickyTop = isPage
    ? `${headerHeight}px`
    : headerHeight != null
      ? `max(calc(${Math.max(0, headerHeight - 57)}px - env(safe-area-inset-top, 0px)), 0px)`
      : undefined;

  return (
    <Tabs
      value={currentTab}
      onValueChange={onTabChange}
      className="w-full pt-2"
    >
      <div
        ref={tabsRef}
        /* Scrolls sideways rather than clipping. Five tabs plus their counts
           can exceed 375px — "Photos 128" on a narrow phone used to have its
           number cut off by `flex-1`'s zero basis, which reads as a broken
           layout rather than as a tab bar that is one tab too wide. */
        className="sticky z-40 [scrollbar-width:none] overflow-x-auto bg-background/80 px-3 pt-2 pb-2 backdrop-blur-sm [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{
          top: stickyTop,
        }}
      >
        <TabsList className="flex w-full min-w-max">
          <TabsTrigger
            value="details"
            className="min-w-max flex-1"
            onClick={onExpandDrawer}
          >
            Details
          </TabsTrigger>
          {showAttendeesTab ? (
            <TabsTrigger
              value="attendees"
              className="min-w-max flex-1"
              onClick={onExpandDrawer}
            >
              Attendees
              {/* The selected date's attendees, not the event's. A day that
                  runs more than once totals every time on it; each time's own
                  count sits on its chip in the slot selector. */}
              <TabCount
                value={
                  occurrencesForSelectedDate.length > 0
                    ? occurrencesForSelectedDate.reduce(
                        (total, occurrence) => total + occurrence.attendeeCount,
                        0,
                      )
                    : (activeOccurrence?.attendeeCount ?? 0)
                }
              />
            </TabsTrigger>
          ) : null}
          {hasModules ? (
            <TabsTrigger
              value="features"
              className="min-w-max flex-1"
              onClick={onExpandDrawer}
            >
              {modulesTabLabel}
            </TabsTrigger>
          ) : null}
          <TabsTrigger
            value="discussion"
            className="min-w-max flex-1"
            // Radix only fires onValueChange when the value changes, so the
            // click handler also lets an already-active tab re-expand.
            onClick={onExpandDrawer}
          >
            Discussion
            <TabCount value={tabCounts.discussion} />
          </TabsTrigger>
          <TabsTrigger
            value="photos"
            className="min-w-max flex-1"
            onClick={onExpandDrawer}
          >
            Photos
            <TabCount value={tabCounts.photos} />
          </TabsTrigger>
        </TabsList>
      </div>

      <div
        // The window scrolls on the page, so hold roughly a screen: a short
        // tab would otherwise yank the column up under the reader.
        className={cn("flex flex-col", isPage && "min-h-[calc(100dvh-10rem)]")}
        style={{
          minHeight:
            !isPage && minContentHeight > 0
              ? `${minContentHeight}px`
              : undefined,
        }}
      >
        <SlotSelectors
          showSlotSelectors={showSlotSelectors}
          dateKeys={dateKeys}
          selectedDateKey={selectedDateKey}
          handleDateChange={handleDateChange}
          occurrencesForSelectedDate={occurrencesForSelectedDate}
          eventPattern={eventPattern}
          selectedOccurrence={selectedOccurrence}
          handleSlotChange={handleSlotChange}
          showAttendeeCounts={currentTab === "attendees"}
        />

        {selectedOccurrence && selectedOccurrence.status !== "scheduled" ? (
          <PastDateNotice
            status={selectedOccurrence.status}
            hasUpcomingDates={hasUpcomingDates}
          />
        ) : null}

        <div className="flex flex-1 flex-col">
          <Activity mode={currentTab === "details" ? "visible" : "hidden"}>
            <Suspense
              fallback={<ListRowSkeleton rowCount={3} className="px-3" />}
            >
              <EventDetailsTabClient
                event={event}
                nextOccurrence={activeOccurrence}
                scheduleOccurrence={activeScheduleOccurrence}
                scheduleOccurrences={scheduleOccurrences}
                hiiveSlug={hiiveSlug}
                distanceKm={distanceKm}
                hiive={hiive}
                hiiveMemberCount={hiiveMemberCount}
                hiiveEventCount={hiiveEventCount}
                packs={packs}
                packStripContext={packStripContext}
                viewerPackHolding={viewerPackHolding}
              />
            </Suspense>
          </Activity>
          <Activity mode={currentTab === "features" ? "visible" : "hidden"}>
            <Suspense
              fallback={<ListRowSkeleton rowCount={3} className="px-3" />}
            >
              <EventActivityTabClient
                nextOccurrence={
                  activeOccurrence
                    ? {
                        id: activeOccurrence.id,
                        startTime: activeOccurrence.startTime,
                        endTime: activeOccurrence.endTime,
                        status: selectedOccurrence?.status ?? "scheduled",
                      }
                    : undefined
                }
                eventName={event.name}
                activities={activities}
                module={module}
                userId={userId}
              />
            </Suspense>
          </Activity>
          <Activity mode={currentTab === "attendees" ? "visible" : "hidden"}>
            <Suspense
              fallback={<ListRowSkeleton rowCount={3} className="px-3" />}
            >
              {activeOccurrence ? (
                canViewAttendeeList ? (
                  <EventMembersTabClient occurrenceId={activeOccurrence.id} />
                ) : canViewAttendeeCount ? (
                  <TabsContent value="attendees" className="mt-4 px-3">
                    <div className="rounded-2xl border border-border/60 bg-surface/60 p-5">
                      <p className="text-xs tracking-[0.18em] text-text-secondary uppercase">
                        Attendee count
                      </p>
                      <p className="mt-2 text-3xl font-semibold text-text-primary">
                        {activeOccurrence.attendeeCount}
                      </p>
                      <p className="mt-2 text-sm text-text-secondary">
                        Attendee names are hidden for this event.
                      </p>
                    </div>
                  </TabsContent>
                ) : null
              ) : (
                <div className="p-8 text-center text-text-secondary">
                  No occurrence selected
                </div>
              )}
            </Suspense>
          </Activity>
          <Activity mode={currentTab === "discussion" ? "visible" : "hidden"}>
            <Suspense
              fallback={<ListRowSkeleton rowCount={3} className="px-3" />}
            >
              <EventDiscussionTabClient
                eventId={event.id}
                viewer={viewer}
                hostUserIds={hostUserIds}
                composerVisible={discussionComposerVisible}
                onExpandRequest={onExpandDrawer}
              />
            </Suspense>
          </Activity>
          <Activity mode={currentTab === "photos" ? "visible" : "hidden"}>
            <Suspense
              fallback={<ListRowSkeleton rowCount={3} className="px-3" />}
            >
              <EventPhotosTabClient
                eventId={event.id}
                viewerAlbumTag={viewerAlbumTag}
                currentUser={
                  viewer?.id
                    ? {
                        id: viewer.id,
                        displayName: viewer.displayName ?? "",
                        avatarUrl: viewer.avatarUrl,
                      }
                    : null
                }
              />
            </Suspense>
          </Activity>
        </div>
      </div>
    </Tabs>
  );
};
